export class ApiError extends Error {
  status: number;
  statusText: string;
  bodyText?: string;

  constructor(args: { status: number; statusText: string; message: string; bodyText?: string }) {
    super(args.message);
    this.name = 'ApiError';
    this.status = args.status;
    this.statusText = args.statusText;
    this.bodyText = args.bodyText;
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string | undefined>;
  body?: unknown;
  signal?: AbortSignal;
};

const buildHeaders = (headers?: Record<string, string | undefined>, hasBody?: boolean) => {
  const h = new Headers();

  if (headers) {
    for (const [key, value] of Object.entries(headers)) {
      if (value !== undefined) h.set(key, value);
    }
  }

  if (hasBody && !h.has('Content-Type')) {
    h.set('Content-Type', 'application/json');
  }

  return h;
};

const formatUrl = (path: string) => {
  if (!path.startsWith('/')) return `/${path}`;
  return path;
};

export const apiClient = {
  async request(path: string, options: RequestOptions = {}): Promise<Response> {
    const hasBody = options.body !== undefined;
    const res = await fetch(formatUrl(path), {
      method: options.method ?? 'GET',
      headers: buildHeaders(options.headers, hasBody),
      body: hasBody ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });

    if (!res.ok) {
      let bodyText: string | undefined;
      try {
        bodyText = await res.text();
      } catch {
        bodyText = undefined;
      }

      throw new ApiError({
        status: res.status,
        statusText: res.statusText,
        message: `Request failed: ${res.status} ${res.statusText}`,
        bodyText,
      });
    }

    return res;
  },

  async getText(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<string> {
    const res = await apiClient.request(path, { ...options, method: 'GET' });
    return res.text();
  },

  async getJson<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    const res = await apiClient.request(path, { ...options, method: 'GET' });
    return res.json() as Promise<T>;
  },

  async postJson<TResponse>(
    path: string,
    body: unknown,
    options: Omit<RequestOptions, 'method' | 'body'> = {}
  ): Promise<TResponse> {
    const res = await apiClient.request(path, { ...options, method: 'POST', body });
    return res.json() as Promise<TResponse>;
  },
};
